import * as vp from 'vopay';
import * as Yup from 'yup';
import { FunctionComponent, useEffect } from 'react';
import { Formik, FormikErrors, FormikTouched } from 'formik';
import styles from './UpdateWorkFlow.module.scss';
import { useTranslation } from 'react-i18next';

const { Box, ElementContainer, DetailsCard, Description, Select, MultiSelector, TextField, Checkbox, Icon, Button } =
  vp;
export type UpdateWorkflow = vp.Workflow & {
  TransactionTypes: string;
  isAll?: boolean;
};
export type UpdateWorkflowContainer = {
  values: UpdateWorkflow;
  errors?: FormikErrors<UpdateWorkflow>;
  touched?: FormikTouched<UpdateWorkflow>;
  submitForm?: () => Promise<void>;
};
export type UpdateWorkFlowFormProps = {
  id: number;
  defaultValues?: UpdateWorkflow;
  transactionTypesResponse?: vp.TransactionTypesResponse;
  workflowsResponse?: vp.WorkFlowsResponse;
  workflowList: (UpdateWorkflowContainer | undefined)[];
  isDelete: boolean;
  isCreate: boolean;
  isView: boolean;
  onDelete: (index: number) => void;
  onChange: (
    id: number,
    values: UpdateWorkflow,
    errors: FormikErrors<UpdateWorkflow>,
    touched: FormikTouched<UpdateWorkflow>,
    submitForm: () => Promise<void>,
  ) => void;
};

type FC = FunctionComponent<UpdateWorkFlowFormProps>;

const UpdateWorkFlowForm: FC = ({
  defaultValues = {
    ID: 0,
    VIOAccounts_ID: 0,
    VIOAccountTransactionTypes_ID: 0,
    NumberApprovalsRequired: 1,
    MinAmount: 0,
    IsActive: '0',
    DateAdded: '',
    TransactionTypes: '',
    isAll: false,
  },
  id,
  isCreate,
  transactionTypesResponse,
  workflowList,
  workflowsResponse,
  isDelete,
  isView,
  onDelete,
  onChange,
}: UpdateWorkFlowFormProps) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'TransactionApproval.Configuration.Workflows.Update.Form',
  });

  return (
    <Formik
      initialValues={{ ...defaultValues }}
      onSubmit={() => {}}
      validationSchema={createdValidationSchema()}
      enableReinitialize
    >
      {({ values, touched, errors, handleChange, handleBlur, submitForm, validateForm }) => {
        useEffect(() => {
          onChange(id, values, errors, touched, submitForm);
        }, [values, errors]);
        useEffect(() => {
          validateForm();
        }, [defaultValues]);
        useEffect(() => {
          if (isCreate) {
            handleChange(vp.createSyntheticEvent('TransactionTypes', ''));
          }
        }, []);

        return (
          <DetailsCard
            width="100%"
            content={
              <Box width="100%" position="relative">
                {isDelete && (
                  <div className={styles.delete}>
                    <Button
                      id={`delete-button-${id}`}
                      type="button"
                      label=""
                      noShadow
                      color="none"
                      icon="trashcan"
                      iconLocation="left"
                      iconHeight="18px"
                      iconWidth="18px"
                      height="18px"
                      width="18px"
                      onClick={() => {
                        onDelete(id);
                      }}
                    />
                  </div>
                )}
                <Box maxwidth="400px" flx="col">
                  <Box pb="sm" flx="col">
                    <ElementContainer
                      padding={isCreate ? '0px 0 0px' : '10px 0 10px'}
                      label={t('PaymentRails.label')}
                      isBold={false}
                      input={
                        <Box flx="col" width="400px">
                          {isCreate && (
                            <Description size="md" color="#5E5E5E">
                              {t('PaymentRails.instruction')}
                            </Description>
                          )}
                          {isCreate && !values.isAll && (
                            <MultiSelector
                              name="TransactionTypes"
                              value={values.TransactionTypes}
                              items={Object.values(transactionTypesResponse?.TransactionTypes || {})
                                .filter((transactionType) => {
                                  const exists = Object.values(workflowsResponse?.Workflows || {}).some(
                                    (workflow) => workflow.VIOAccountTransactionTypes_ID === transactionType.ID,
                                  );
                                  const existsInOther = workflowList.some(
                                    (workflow, index) =>
                                      index !== id &&
                                      (workflow?.values?.TransactionTypes || '')
                                        .split('|')
                                        .some(
                                          (transactionTypeWorkflow) =>
                                            transactionTypeWorkflow === transactionType.TransactionType,
                                        ),
                                  );
                                  return !exists && !existsInOther;
                                })
                                .map((transactionType) => ({
                                  label: transactionType.TransactionType,
                                  value: transactionType.TransactionType,
                                  checked: values.TransactionTypes.includes(transactionType.TransactionType),
                                }))}
                              labelPosition="right"
                              labelSize="16px"
                              identifier={id.toString()}
                              setFieldValue={(field: string, value: string) => {
                                handleChange(vp.createSyntheticEvent(field, value));
                              }}
                              variant="outlined"
                              height="37px"
                              indicatorMinHeight="34px"
                              onBlur={handleBlur}
                              error={
                                errors.TransactionTypes && touched.TransactionTypes ? errors.TransactionTypes : null
                              }
                            />
                          )}
                          {(!isCreate || values.isAll) && (
                            <TextField
                              width="100%"
                              containerWidth="100%"
                              disabled
                              variant="background"
                              value={values.TransactionTypes.split('|').join('; ')}
                              name={`appover-rails-${id}`}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={
                                errors.TransactionTypes && touched.TransactionTypes ? errors.TransactionTypes : null
                              }
                            />
                          )}
                          {isCreate &&
                            Object.values(transactionTypesResponse?.TransactionTypes || {})
                              .filter((transactionType) => {
                                const exists = Object.values(workflowsResponse?.Workflows || {}).some(
                                  (workflow) => workflow.VIOAccountTransactionTypes_ID === transactionType.ID,
                                );
                                const existsInOther = workflowList.some(
                                  (workflow, index) =>
                                    index !== id &&
                                    (workflow?.values?.TransactionTypes || '')
                                      .split('|')
                                      .some(
                                        (transactionTypeWorkflow) =>
                                          transactionTypeWorkflow === transactionType.TransactionType,
                                      ),
                                );
                                return !exists && !existsInOther;
                              })
                              .map((transactionType) => ({
                                label: transactionType.TransactionType,
                                checked: values.TransactionTypes.includes(transactionType.TransactionType),
                              })).length > 1 &&
                            workflowList.length === 1 && (
                              <Box pt="sm">
                                <Checkbox
                                  name="isAll"
                                  value={false}
                                  labelPosition="right"
                                  hasNoPadding
                                  labelSize="14px"
                                  label={t('PaymentRails.selectAllCheckbox')}
                                  onBlur={handleBlur}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      const newValue = Object.values(transactionTypesResponse?.TransactionTypes || {})
                                        .filter((transactionType) => {
                                          const exists = Object.values(workflowsResponse?.Workflows || {}).some(
                                            (workflow) => workflow.VIOAccountTransactionTypes_ID === transactionType.ID,
                                          );
                                          return !exists;
                                        })
                                        .map((transactionType) => transactionType.TransactionType)
                                        .join('|');
                                      handleChange(vp.createSyntheticEvent('TransactionTypes', newValue));
                                    } else {
                                      handleChange(vp.createSyntheticEvent('TransactionTypes', ''));
                                    }
                                    handleChange(e);
                                  }}
                                />
                              </Box>
                            )}
                        </Box>
                      }
                    />
                  </Box>
                  <Box pb="sm" flx="col">
                    <ElementContainer
                      label={t('NumberOfApprovers.label')}
                      padding="14px 0 12px"
                      isBold={false}
                      input={
                        <Select
                          options={{
                            '1': '1',
                            '2': '2',
                            '3': '3',
                            '4': '4',
                            '5': '5',
                            '6': '6',
                            '7': '7',
                            '8': '8',
                            '9': '9',
                            '10': '10',
                          }}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          disabled={isView}
                          noOrdering
                          error={
                            errors.NumberApprovalsRequired && touched.NumberApprovalsRequired
                              ? errors.NumberApprovalsRequired
                              : null
                          }
                          placeholder={t('NumberOfApprovers.placeholder')}
                          variant="outlined"
                          value={
                            values.NumberApprovalsRequired !== null ? values.NumberApprovalsRequired.toString() : ''
                          }
                          name="NumberApprovalsRequired"
                        />
                      }
                    />
                  </Box>
                  <Box pb="sm" flx="col">
                    <ElementContainer
                      label={t('MinimumTransactionAmount.label')}
                      padding="14px 0 0px"
                      isBold={false}
                      input={
                        <Box flx="col" width="100%">
                          <Description size="md" color="#5E5E5E">
                            {t('MinimumTransactionAmount.instruction')}
                          </Description>
                          <TextField
                            name="MinAmount"
                            value={isView ? `$${vp.formattedCurrency(values.MinAmount.toString())}` : values.MinAmount}
                            preset={isView ? 'text' : 'number'}
                            variant={isView ? 'background' : undefined}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            disabled={isView}
                            error={errors.MinAmount && touched.MinAmount ? errors.MinAmount : null}
                            elementLeft={
                              !isView ? (
                                <Box ps="sm">
                                  <Icon icon="dollar" position="start" color="medium-grey" size="sm" width="17px" />
                                </Box>
                              ) : undefined
                            }
                          />
                        </Box>
                      }
                    />
                  </Box>
                </Box>
              </Box>
            }
          />
        );
      }}
    </Formik>
  );
};

function createdValidationSchema() {
  return Yup.object().shape({
    MinAmount: Yup.string().required(),
    NumberApprovalsRequired: Yup.string().required(),
    TransactionTypes: Yup.string().required(),
  });
}
export default UpdateWorkFlowForm;
